import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, FormControl, InputLabel, Select, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Download, AccountBalance, Payment, TrendingDown, Calculate, Refresh } from '@mui/icons-material';
import { balloonPaymentData, loanInputData } from '../data/mockData';
import { Restore as RestoreIcon, Edit as EditIcon } from "@mui/icons-material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import '../styles/BalloonPayment.scss';
import '../styles/loanCommon.scss';
import LoanInputParameters from './LoanInputParameters';
import { trackActivity } from '../../../../utils/mixpanel';

const BalloonPayment = ({ loanType, onLoanTypeChange }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Balloon Payment', page_type: page_type })
    }

    const [scheduleData] = useState(balloonPaymentData);
    const [inputData, setInputData] = useState({
        ...loanInputData,
        balloonAmount: 2000000,
        balloonPercentage: 20,
        balloonPaymentDate: '2029-12-01'
    });
    const [error, setError] = useState({});
    const [openDialog, setOpenDialog] = useState({ modal: false });

    const handleInputChange = (field, value) => {
        setInputData({ ...inputData, [field]: value });
    };

    const onRestErrorKey = (key) => {
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const handleCalculate = () => {
        alert('Balloon calculation logic will be implemented');
    };

    const handleExport = (format) => {
        alert(`Exporting schedule as ${format}`);
    };

    const handleSaveBalloonPayment = () => {
        alert('Added successfully');
        setOpenDialog(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const amortizedAmount = inputData.advanceAmount - inputData.balloonAmount;

    return (
        <Box className="balloon-payment-container">
            {/* Summary Cards */}
            <Grid container spacing={3} className="summary-cards">
                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <AccountBalance className="card-icon" style={{ color: '#f59e0b' }} />
                                <Typography variant="subtitle2" className="card-label">Balloon Amount</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(inputData.balloonAmount)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Payable at Month 60
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <TrendingDown className="card-icon" style={{ color: '#3b82f6' }} />
                                <Typography variant="subtitle2" className="card-label">Amortized Amount</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(amortizedAmount)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Over 60 months
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <Payment className="card-icon" style={{ color: '#10b981' }} />
                                <Typography variant="subtitle2" className="card-label">Monthly Payment</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                $154,371
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Excluding balloon
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <Payment className="card-icon" style={{ color: '#ef4444' }} />
                                <Typography variant="subtitle2" className="card-label">Final Payment</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                $2,001,453
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Balloon + Interest
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card className="input-form-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Loan Input Parameters</Typography>
                        <Box className="export-buttons">
                            <Tooltip arrow title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => handleItemClicked('RevisionHistoryIcon')} fontSize='small' color='primary' /></Tooltip>
                            <Tooltip arrow title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { setOpenDialog({ modal: true }); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <LoanInputParameters inputData={inputData} handleInputChange={handleInputChange} loanType={loanType} onLoanTypeChange={onLoanTypeChange} type={'view'} />
                    </Grid>
                </CardContent>
            </Card>

            {/* Schedule Table */}
            <Card className="table-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Balloon Payment Schedule</Typography>
                        <Box className="export-buttons">
                            <Button startIcon={<ArrowDownwardIcon />} variant="outlined" size='small' onClick={() => handleItemClicked('Export Button')}> Export </Button>
                        </Box>
                    </Box>
                    <TableContainer component={Paper} className="balloon-table lease-detail-table">
                        <Table className="mui-table-format" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell  sx={{minWidth: '130px'}}>Month</TableCell>
                                    <TableCell  sx={{minWidth: '130px'}}>Date</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Debt Service</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Interest</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Principal</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Balloon Payment</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Outstanding</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleData.map((row) => (
                                    <TableRow
                                        key={row.month}
                                        hover
                                        className={row.balloonPayment ? 'balloon-row' : ''}
                                    >
                                        <TableCell>{row.month}</TableCell>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell align="left"  sx={{minWidth: '130px'}}>{formatCurrency(row.debtService)}</TableCell>
                                        <TableCell align="left"  sx={{minWidth: '130px'}}>{formatCurrency(row.interest)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.principalRepayment)}</TableCell>
                                        <TableCell align="left">
                                            {row.balloonPayment ? (
                                                <Chip
                                                    label={formatCurrency(row.balloonPayment)}
                                                    color="error"
                                                    size="small"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="left" className="outstanding-balance">
                                            {formatCurrency(row.principalOutstanding)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {openDialog?.modal ? <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' maxWidth='md' open={true}>
                <DialogTitle className='dialog-header'>Edit Balloon Payment</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <LoanInputParameters type={'edit'} inputData={inputData} loanType={loanType} onLoanTypeChange={onLoanTypeChange} onRestErrorKey={onRestErrorKey} error={error}/>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenDialog({ modal: false }); handleItemClicked('Cancel Button') }} size='small'>Cancel</Button>
                    <Button onClick={() => { handleSaveBalloonPayment(); handleItemClicked('Save Button') }} variant="contained" color="primary" size='small'>Save</Button>
                </DialogActions>
            </Dialog> : null}
        </Box>
    );
};

export default BalloonPayment;

